import { useEffect, useRef, useState } from 'react';
import { useOrgRates } from '../../../hooks/useOrgRates';
import type { Organization, ResourceRate, TravelTimeRate } from '../../../types/admin';
import type { ItemizationPreference } from '@iomi/shared';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { useOrgWarehouses } from '../../../hooks/useOrgWarehouses';
import { PORTAL_TO_PORTAL_NO_WAREHOUSE_WARNING, SETUP_EXPLAINERS } from '@iomi/shared';
import { CrewRatesCard } from './CrewRatesCard';
import {
  QuoteBuildingCard,
  ShiftSettingsCard,
} from '../../admin/QuoteDefaultsPage';
import type { OrgSettingsUpdate } from '../../../hooks/useOrgSettings';
import { api } from '../../../lib/api';

// ── Types ────────────────────────────────────────────────────────────────

export interface CommercialSettingsData {
  rates: Record<string, number>;
  useBlendedTruckDriverRate: boolean;
  travelTimeRate: TravelTimeRate;
  fixedTravelTimeHours: number;
  efficiencyMultiplier: number;
  quoteValidityDays: number | null;
  localMoveRadiusType: 'distance' | 'time';
  localMoveRadiusValue: number;
  shiftSettings: { name: string; lengthHours: number }[];
  itemizationPreference: ItemizationPreference;
  /** Name of a card still in edit mode (its unsaved changes would be lost on Continue), or null. */
  openEditor: string | null;
}

interface CommercialSettingsStepProps {
  org: Organization;
  orgId: string;
  onChange: (data: CommercialSettingsData) => void;
  /** Highlight required fields that block Continue (set after a blocked attempt). */
  showErrors?: boolean;
}

// ── Main component ───────────────────────────────────────────────────────

export function CommercialSettingsStep({ org, orgId, onChange, showErrors = false }: CommercialSettingsStepProps) {
  const { rates, loading: ratesLoading } = useOrgRates(orgId);
  // Crew rates are shared across move types. The note belongs on whichever
  // rates step comes first: Residential Settings precedes this step, so it
  // already showed the note unless residential bills flat rates (no crew rates
  // card there). Only then does this step carry it.
  const sharedRatesBanner = org.featureResidentialMovingQuotes === true
    && org.residentialPricingModel === 'flat_rate';

  // Rates
  const rateMap = useRef<Record<string, number>>({});
  const [supervisorRate, setSupervisorRate] = useState(0);
  const [moverRate, setMoverRate] = useState(0);
  const [driverRate, setDriverRate] = useState(0);
  const [truckRate, setTruckRate] = useState(0);
  const [truckDriverRate, setTruckDriverRate] = useState(0);
  const [useBlended, setUseBlended] = useState(org.useBlendedTruckDriverRate ?? true);

  // Quote defaults — tracked via a virtual org for the admin card components
  const [virtualOrg, setVirtualOrg] = useState<Organization>({ ...org });
  // Cards left in edit mode: Continue warns instead of silently discarding them.
  const [openEditors, setOpenEditors] = useState<Record<string, boolean>>({});
  const openEditor = Object.keys(openEditors).find(k => openEditors[k]) ?? null;
  const trackEditor = (name: string) => (editing: boolean) =>
    setOpenEditors(prev => ({ ...prev, [name]: editing }));
  // Portal to Portal needs a warehouse to measure travel from; warn where the choice is made.
  const { warehouses, loading: warehousesLoading } = useOrgWarehouses(orgId);

  // Seed rates
  useEffect(() => {
    if (rates.length === 0) return;
    const bySlug = Object.fromEntries(rates.map((r: ResourceRate) => [r.slug, r.hourlyRate]));
    rateMap.current = bySlug;
    setSupervisorRate(bySlug.supervisor ?? 0);
    setMoverRate(bySlug.mover ?? 0);
    setDriverRate(bySlug.driver ?? 0);
    setTruckRate(bySlug.truck ?? 0);
    setTruckDriverRate(bySlug.truck_driver ?? 0);
  }, [rates]);

  // Derive values from virtual org for the parent onChange
  const travelTimeRate = virtualOrg.commercialDefaultTravelTimeRate ?? virtualOrg.defaultTravelTimeRate ?? 'one_hour_standard';
  const fixedTravelTimeHours = virtualOrg.commercialFixedTravelTimeHours ?? virtualOrg.fixedTravelTimeHours ?? 1;
  const efficiencyMultiplier = virtualOrg.commercialEfficiencyMultiplier ?? virtualOrg.efficiencyMultiplier ?? 1;
  // null means "Never expires" and must survive; only an unset value falls back.
  const quoteValidityDays: number | null = virtualOrg.commercialDefaultQuoteValidityDays !== undefined
    ? virtualOrg.commercialDefaultQuoteValidityDays
    : (virtualOrg.defaultQuoteValidityDays ?? 30);
  const localRadiusType = virtualOrg.localMoveRadiusType ?? 'distance';
  const localRadiusValue = virtualOrg.localMoveRadiusValue ?? 50;
  const shiftSettings = virtualOrg.commercialShiftSettings ?? virtualOrg.residentialShiftSettings ?? [{ name: 'Standard', lengthHours: 8 }];
  const itemization: ItemizationPreference = ((virtualOrg as unknown as Record<string, unknown>).commercialItemizationPreference as ItemizationPreference) ?? 'single_line';

  // Notify parent
  useEffect(() => {
    onChange({
      rates: {
        supervisor: supervisorRate,
        mover: moverRate,
        driver: driverRate,
        truck: truckRate,
        truck_driver: truckDriverRate,
      },
      useBlendedTruckDriverRate: useBlended,
      travelTimeRate,
      fixedTravelTimeHours,
      efficiencyMultiplier,
      quoteValidityDays,
      localMoveRadiusType: localRadiusType,
      localMoveRadiusValue: localRadiusValue,
      shiftSettings,
      itemizationPreference: itemization,
      openEditor,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supervisorRate, moverRate, driverRate, truckRate, truckDriverRate, useBlended,
    travelTimeRate, fixedTravelTimeHours, efficiencyMultiplier, quoteValidityDays, localRadiusType, localRadiusValue,
    shiftSettings, itemization, openEditor]);

  // Handle saves from admin card components — update virtual org
  async function handleQuoteBuildingSave(data: OrgSettingsUpdate) {
    setVirtualOrg(prev => ({ ...prev, ...data } as Organization));
  }

  async function handleShiftSettingsSave(data: OrgSettingsUpdate) {
    setVirtualOrg(prev => ({ ...prev, ...data } as Organization));
  }

  if (ratesLoading) {
    return <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* ── Crew Rates ── */}
      <CrewRatesCard
        type="commercial"
        supervisorRate={supervisorRate}
        moverRate={moverRate}
        driverRate={driverRate}
        truckRate={truckRate}
        truckDriverRate={truckDriverRate}
        useBlended={useBlended}
        onSupervisorChange={setSupervisorRate}
        onMoverChange={setMoverRate}
        onDriverChange={setDriverRate}
        onTruckChange={setTruckRate}
        onTruckDriverChange={setTruckDriverRate}
        onBlendedChange={setUseBlended}
        sharedRatesBanner={sharedRatesBanner}
        showErrors={showErrors}
      />

      {/* ── Quote Building (reuses admin component) ── */}
      <QuoteBuildingCard
        type="commercial"
        org={virtualOrg}
        onSave={handleQuoteBuildingSave}
        onEditingChange={trackEditor('Quote Building')}
        hints={{
          travelTime: SETUP_EXPLAINERS.travelTime.body,
          localRadius: SETUP_EXPLAINERS.localMoveRadius.body,
          efficiency: SETUP_EXPLAINERS.efficiencyMultiplier.body,
          validity: SETUP_EXPLAINERS.quoteValidity.body,
        }}
      />
      {travelTimeRate === 'portal_to_portal' && !warehousesLoading && warehouses.length === 0 && (
        <div role="alert" className="rounded-xl bg-amber-50 border border-amber-200 p-4">
          <p className="text-sm text-amber-900">{PORTAL_TO_PORTAL_NO_WAREHOUSE_WARNING}</p>
          <p className="text-xs text-amber-800 mt-1">
            Use Back to add one on the Warehouse &amp; Trucks step, or later under Operations → Manage Warehouses.
          </p>
        </div>
      )}

      {/* ── Shift Settings (reuses admin component) ── */}
      <ShiftSettingsCard
        type="commercial"
        org={virtualOrg}
        onSave={handleShiftSettingsSave}
        onEditingChange={trackEditor('Shift Settings')}
        hint={SETUP_EXPLAINERS.shifts.body}
      />

      {/* ── Itemization (set under Quoting → Quote Defaults; explained here) ── */}
      <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="px-4 py-3.5">
          <p className="font-semibold text-small text-midnight">{SETUP_EXPLAINERS.itemization.title}</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Current setting: {itemization === 'single_line' ? 'Single Line' : itemization === 'crew_trucks' ? 'Crew & Trucks' : itemization === 'duration_transit' ? 'Duration & Transit' : 'Flat Rate Add-ons'}
          </p>
          <p className="text-[12px] text-slate leading-snug mt-2">{SETUP_EXPLAINERS.itemization.body}</p>
        </div>
      </section>

      {/* ── Inventory & Services ── */}
      <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="px-4 py-3.5 border-b border-stone">
          <p className="font-semibold text-small text-midnight">Inventory & Services</p>
          <p className="text-[12px] text-slate leading-snug mt-0.5">
            Pre-loaded with IOMI defaults
          </p>
        </div>
        <div className="px-4 py-4">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 mb-4">
            <p className="text-[12px] text-emerald-800">
              Your commercial inventory catalog (IOMI Casegoods methodology) and scope of services
              have been pre-loaded. You can customize these anytime in your Quoting settings.
            </p>
          </div>
          <InventoryCounts orgId={orgId} />
        </div>
      </section>
    </div>
  );
}

// ── Inventory counts ─────────────────────────────────────────────────────

function InventoryCounts({ orgId }: { orgId: string }) {
  const [counts, setCounts] = useState<{ items: number; services: number } | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<{ success: boolean; data: unknown[] }>(`/org-admin/${orgId}/inventory`).then(r => r.data.length).catch(() => 0),
      api.get<{ success: boolean; data: unknown[] }>(`/org-admin/${orgId}/services`).then(r => r.data.length).catch(() => 0),
    ]).then(([items, services]) => setCounts({ items, services }));
  }, [orgId]);

  if (!counts) return null;

  return (
    <div className="flex gap-4 text-center">
      <div className="flex-1 py-2 rounded-lg bg-mist">
        <p className="text-lg font-semibold text-midnight">{counts.items}</p>
        <p className="text-[11px] text-slate">Items</p>
      </div>
      <div className="flex-1 py-2 rounded-lg bg-mist">
        <p className="text-lg font-semibold text-midnight">{counts.services}</p>
        <p className="text-[11px] text-slate">Services</p>
      </div>
    </div>
  );
}
